import { z } from 'zod';
import { actionSchema } from './actions';

/**
 * Promotion policy FORM schema (camelCase UI representation).
 *
 * Maps to the generated `PromotionPolicyToken` (PascalCase) via the mapper in
 * src/lib/mappers/promotion.ts. Field-name correspondence (token ← form):
 *   Name←name, Code←code, Priority←priority, Description←description,
 *   BadgeText←badgeText, BadgeImageUrl←badgeImageUrl,
 *   BackgroundImageUrl←backgroundImageUrl, FrontLabel←frontLabel (multilanguage server-side,
 *   written to the active Accept-Language like Name/Description), ColorHex←colorHex,
 *   BackgroundColorHex←backgroundColorHex, ExternalUrl←externalUrl, IsActive←isActive,
 *   iv←isVisible, HideFromOffers←hideFromOffers (constraint #5 — two flags),
 *   ActiveFrom←activeFrom, ActiveTo←activeTo, MinAmount←minAmount,
 *   MaxAmount←maxAmount, MinQuantity←minQuantity,
 *   AppliesToSelectedProducts←appliesToSelectedProducts,
 *   ProductMatchCondition←productMatchCondition (constraint #1 inner),
 *   ProductSelectionConditionMode←productSelectionConditionMode (constraint #1 outer),
 *   ApplyToSiteMode←applyToSiteMode + SiteIds←siteIds (constraint #2),
 *   ProductIds/CategoryIds/ManufacturersIds/CustomerCategoryIds/CustomerIds/
 *   CustomerGroupsIds, CartActions←actions.
 *
 * Combinators are stored as friendly strings here and converted to the backend
 * `ConditionCombinationMode` enum (Or=1/And=2) in the mapper. The inner combinator
 * uses any/all labels but the SAME Or/And enum on the backend (verified: there is
 * no separate Any/All enum — Core/client/api/models/condition-combination-mode.ts).
 */

const CODE_REGEX = /^[A-Z0-9_-]+$/;

/** Inner combinator (on the Products selection). any → Or (∨, ≥1 in cart), all → And (∧, all in cart). */
export const productMatchConditionSchema = z.enum(['any', 'all']);

/** Outer combinator (across Products / Categories / Manufacturers blocks). */
export const conditionsCombinationSchema = z.enum(['or', 'and']);

/** Site scope (used only when siteIds is empty — constraint #2 strict branch). */
export const applyToSiteModeSchema = z.enum(['all', 'default']);

export const promotionSchema = z
	.object({
		id: z.number().optional(),

		// Identity
		name: z.string().min(3).max(100),
		code: z.string().min(1).max(50).regex(CODE_REGEX),
		priority: z.number().int().min(0).max(999),

		// Description & badge
		description: z.string().max(500).nullable().default(null),
		badgeText: z.string().max(20).nullable().default(null),
		badgeImageUrl: z.string().nullable().default(null),
		backgroundImageUrl: z.string().nullable().default(null),

		// Storefront label styling + link (BW-8300)
		frontLabel: z.string().max(100).nullable().default(null),
		colorHex: z.string().max(10).nullable().default(null),
		backgroundColorHex: z.string().max(10).nullable().default(null),
		externalUrl: z.string().max(2000).nullable().default(null),

		// Status & visibility — constraint #5: two independent flags
		isActive: z.boolean().default(true),
		isVisible: z.boolean().default(true),
		hideFromOffers: z.boolean().default(false),

		// Time window
		activeFrom: z.date().nullable().default(null),
		activeTo: z.date().nullable().default(null),

		// Cart thresholds
		minAmount: z.number().nonnegative().nullable().default(null),
		maxAmount: z.number().nonnegative().nullable().default(null),
		minQuantity: z.number().int().nonnegative().nullable().default(null),
		appliesToSelectedProducts: z.boolean().default(false),

		// Product selection — constraint #1: two SEPARATE combinator fields
		productMatchCondition: productMatchConditionSchema.default('any'),
		productSelectionConditionMode: conditionsCombinationSchema.default('and'),
		productIds: z.array(z.number()).default([]),
		categoryIds: z.array(z.number()).default([]),
		manufacturerIds: z.array(z.number()).default([]),

		// Customer restriction
		customerCategoryIds: z.array(z.number()).default([]),
		customerIds: z.array(z.number()).default([]),
		customerGroupIds: z.array(z.number()).default([]),

		// Site scope — constraint #2: siteIds populated wins; else applyToSiteMode
		siteIds: z.array(z.number()).default([]),
		applyToSiteMode: applyToSiteModeSchema.default('all'),

		// Actions
		actions: z.array(actionSchema).default([])
	})
	.refine(p => !(p.activeFrom && p.activeTo) || p.activeTo >= p.activeFrom, {
		message: 'activeToBeforeActiveFrom',
		path: ['activeTo']
	})
	.refine(p => p.maxAmount == null || p.minAmount == null || p.maxAmount >= p.minAmount, {
		message: 'maxAmountBelowMinAmount',
		path: ['maxAmount']
	});

export type PromotionFormValues = z.infer<typeof promotionSchema>;

/** A blank policy for the create flow (/promotions/new). id 0 ⇒ create on save. */
export function emptyPromotionForm(): PromotionFormValues {
	return {
		id: 0,
		name: '',
		code: '',
		priority: 1,
		description: null,
		badgeText: null,
		badgeImageUrl: null,
		backgroundImageUrl: null,
		frontLabel: null,
		colorHex: null,
		backgroundColorHex: null,
		externalUrl: null,
		isActive: true,
		isVisible: true,
		hideFromOffers: false,
		activeFrom: null,
		activeTo: null,
		minAmount: null,
		maxAmount: null,
		minQuantity: null,
		appliesToSelectedProducts: false,
		productMatchCondition: 'any',
		productSelectionConditionMode: 'and',
		productIds: [],
		categoryIds: [],
		manufacturerIds: [],
		customerCategoryIds: [],
		customerIds: [],
		customerGroupIds: [],
		siteIds: [],
		applyToSiteMode: 'all',
		actions: []
	};
}
